import React, { useRef, useState } from 'react';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/imageUrl';
import { toast } from 'sonner';
import { Upload, X, Loader2 } from 'lucide-react';

/**
 * Reusable image input + uploader.
 * - Displays a preview of the current value
 * - Lets admin paste a URL OR upload a file (drag-and-drop)
 * Props: value, onChange (string), testId
 */
const ImageUploader = ({ value, onChange, testId, label = "Image" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image (JPEG/PNG/WEBP/GIF).');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 8 MB).');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/admin/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      toast.success('Image uploadée');
    } catch (err) {
      console.error(err);
      toast.error("Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testId}>
      {value ? (
        <div className="relative group border border-[#111111]/10 bg-[#F5F5F5] mb-3">
          <img src={resolveImageUrl(value)} alt={label} className="w-full h-48 object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 w-8 h-8 bg-white/95 border border-[#111111]/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:border-red-500 hover:text-red-500"
            data-testid={testId ? `${testId}-remove` : undefined}
            aria-label="Retirer l'image"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : null}

      <div
        className={`border border-dashed ${dragOver ? 'border-[#111111] bg-[#111111]/5' : 'border-[#111111]/30 bg-[#F5F5F5]/40'} p-6 text-center transition-colors cursor-pointer mb-3`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-3 text-[#6B6B6B] text-xs uppercase tracking-[0.3em]">
            <Loader2 className="w-4 h-4 animate-spin" /> Upload…
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-[#6B6B6B]">
            <Upload className="w-6 h-6" />
            <span className="text-xs uppercase tracking-[0.3em]">Glisser une image ou cliquer</span>
            <span className="text-[10px] text-[#6B6B6B]/60">JPEG / PNG / WEBP / GIF — max 8 MB</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={testId ? `${testId}-file-input` : undefined}
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B]/70 mb-1 block">…ou coller une URL d'image</label>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… ou /api/files/…"
          className="w-full border border-[#111111]/20 bg-white px-4 py-2 text-sm font-serif focus:border-[#111111] focus:outline-none transition-colors"
          data-testid={testId ? `${testId}-url-input` : undefined}
        />
      </div>
    </div>
  );
};

export default ImageUploader;
